import './styles.scss'
import React, { useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min'
import { useTranslation } from 'react-i18next'
import apiAdapterCoffeeWeb from 'Services/apiAdapterCoffeeWeb'
import Loader from 'Components/Loader'
import LayoutContainer from 'Components/LayoutComponent'
import { DialogModal, ReportColumnSettingsDialog } from 'Components/UIComponents'
import YearMonthNavigation from 'Components/UIComponents/YearMonthNavigation'
import { commonMethods } from 'Utils/commonMethods'
import { ROUTE_STRINGS } from 'Utils/Constants'
import { getUserRoleBasedFeature } from 'Utils/RolesPermission'
import { getLocalSubscriptionPaymentReportTableColumns, setLocalWithSubscriptionPaymentReportTableColumns } from 'Utils/LocalStorageHandler'
import { exportReportToExcel } from 'Utils/reportExportUtils'
import { useReportColumnSettings } from 'Utils/reportColumnSettingsUtils'

const defaultColumnsVisibility = {
  idOrder: true,
  invoiceNumber: true,
  subName: true,
  invoiceAmount: true,
  paymentStatus: true,
  paymentSource: true,
  paymentDate: true,
  transactionId: true
}

const SubscriptionPaymentReport = ({ setLoading, isLoading }) => {
  const history = useHistory()
  const location = useLocation()
  const { t } = useTranslation()
  const userRoleBasedFeature = getUserRoleBasedFeature()
  const showColumn = userRoleBasedFeature?.functionality?.paymentReport?.showColumn ?? false

  const columnSettings = useReportColumnSettings({
    getStored: getLocalSubscriptionPaymentReportTableColumns,
    setStored: setLocalWithSubscriptionPaymentReportTableColumns,
    defaultVisibility: defaultColumnsVisibility,
    minVisibleColumns: 4
  })

  const [fetchedYearData, setFetchedYearData] = useState([])
  const [fetchedMonthData, setFetchedMonthData] = useState([])
  const [yearList, setYearList] = useState([])
  const [filterValue, setFilterValue] = useState([])
  const [selectedYear, setSelectedYear] = useState(null)
  const [selectedYearData, setSelectedYearData] = useState(null)
  const [selectedYearIndex, setSelectedYearIndex] = useState(null)
  const [selectedMonthData, setSelectedMonthData] = useState(null)
  const [dateRange, setDateRange] = useState(null)
  const [searchedValue, setSearchedValue] = useState(null)
  const [invoiceNumberSearch, setInvoiceNumberSearch] = useState(null)
  const [filterStates, setFilterStates] = useState({})
  const [showDialog, setShowDialog] = useState({ showModal: false, modalType: '', message: '', onHide: () => {}, onRetry: () => {}, onConfirmation: () => {}, onSuccess: () => {} })

  useEffect(() => {
    if (fetchedYearData.length === 0) return

    const urlParams = new URLSearchParams(location.search)
    const yearParam = urlParams.get('year')
    const monthParam = urlParams.get('month')

    const yearIndex = fetchedYearData?.findIndex((item) => item?.year.toString() === yearParam)

    if (yearIndex !== -1 && yearIndex !== undefined) {
      setSelectedYearIndex(yearIndex)
      const yearData = fetchedYearData[yearIndex]

      setSelectedYearData(yearData)
      setSelectedYear(yearData?.year)

      if (monthParam) {
        const foundMonth = yearData?.months?.find((m) => m.name.toLowerCase() === monthParam.toLowerCase())

        if (foundMonth) {
          setSelectedMonthData(foundMonth)
          getPaymentsByMonthReport(yearData, foundMonth)
        } else {
          setSelectedMonthData(null)
          setFetchedMonthData([])
          setDateRange(null)
          setFilterStates({})
          setFilterValue([])
          setSearchedValue(null)
          setInvoiceNumberSearch(null)
        }
      } else {
        setSelectedMonthData(null)
        setFetchedMonthData([])
        setDateRange(null)
        setFilterStates({})
        setFilterValue([])
        setSearchedValue(null)
        setInvoiceNumberSearch(null)
      }
    } else {
      setSelectedYearData(null)
      setSelectedYearIndex(null)
      setSelectedYear(null)
      setSelectedMonthData(null)
      setFetchedMonthData([])
      setDateRange(null)
      setFilterStates({})
      setFilterValue([])
      setSearchedValue(null)
      setInvoiceNumberSearch(null)
    }
  }, [fetchedYearData, location.search])

  useEffect(() => {
    fetchAllSubscriptionPaymentYears()
  }, [])

  useEffect(() => {
    columnSettings.initStorageIfEmpty()
  }, [])

  const fetchAllSubscriptionPaymentYears = async () => {
    setLoading(true)
    setShowDialog({ ...showDialog, showModal: false })

    try {
      const response = await apiAdapterCoffeeWeb.getAllSubscriptionPaymentReport()
      const yearResponse = response?.data?.returnLst
      const yearData = yearResponse?.map((item) => ({
        year: item.year
      }))

      if (yearResponse) {
        setFetchedYearData(yearResponse)
        setYearList(yearData)
      }
    } catch {
      setShowDialog({
        ...showDialog,
        showModal: true,
        modalType: 'error',
        message: t('FAILED_TO_FETCH_PAYMENT_REPORT'),
        onRetry: () => fetchAllSubscriptionPaymentYears(),
        onHide: handleDismiss
      })
    } finally {
      setLoading(false)
    }
  }

  const getPaymentsByMonthReport = async (yearData, monthData) => {
    const { year } = yearData
    const { name } = monthData

    setLoading(true)
    setShowDialog({ ...showDialog, showModal: false })

    const retryFunction = () => getPaymentsByMonthReport(yearData, monthData)

    try {
      const response = await apiAdapterCoffeeWeb.fetchPaymentReportsByMonth({
        Year: year,
        Month: name
      })
      const monthResponse = response?.data?.returnLst

      const modifiedData = monthResponse?.map((item) => {
        const paymentDate = commonMethods.utcToLocalTime(item.paymentDate)

        return {
          ...item,
          paymentDate
        }
      })

      setFetchedMonthData(modifiedData)
    } catch {
      setShowDialog({
        ...showDialog,
        showModal: true,
        modalType: 'error',
        message: t('ERROR_FETCHING_PAYMENT_REPORT_FOR_MONTH'),
        onRetry: () => retryFunction(),
        onHide: handleDismiss
      })
    } finally {
      setLoading(false)
    }
  }

  const handleYearClick = (item, index) => {
    const yearData = fetchedYearData?.map((item) => ({
      year: item.year
    }))

    setYearList(yearData)
    setSelectedYearIndex(index)
    setSelectedYearData(fetchedYearData[index])
    setSelectedYear(fetchedYearData[index]?.year)

    const updatedParams = new URLSearchParams(location.search)

    updatedParams.set('year', item.year)
    updatedParams.delete('month')

    history.push({
      pathname: ROUTE_STRINGS.subscriptionPaymentReport,
      search: updatedParams.toString()
    })
  }

  const handleMonthClick = (month) => {
    setSelectedMonthData(month)

    const updatedParams = new URLSearchParams(location.search)

    updatedParams.set('month', month.name)

    history.push({
      pathname: ROUTE_STRINGS.subscriptionPaymentReport,
      search: updatedParams.toString()
    })
  }

  const handleNext = () => {
    if (selectedYearIndex < fetchedYearData.length - 1) {
      const newIndex = selectedYearIndex + 1

      setSelectedYearIndex(newIndex)
      setSelectedYearData(fetchedYearData[newIndex])

      setSelectedYear(fetchedYearData[newIndex]?.year)

      const updatedParams = new URLSearchParams(location.search)

      updatedParams.set('year', fetchedYearData[newIndex].year)
      updatedParams.delete('month')

      history.push({
        pathname: ROUTE_STRINGS.subscriptionPaymentReport,
        search: updatedParams.toString()
      })
    }
  }

  const handlePrevious = () => {
    if (selectedYearIndex > 0) {
      const newIndex = selectedYearIndex - 1

      setSelectedYearIndex(newIndex)
      setSelectedYearData(fetchedYearData[newIndex])
      setSelectedYear(fetchedYearData[newIndex].year)

      const updatedParams = new URLSearchParams(location.search)

      updatedParams.set('year', fetchedYearData[newIndex].year)
      updatedParams.delete('month')

      history.push({
        pathname: ROUTE_STRINGS.subscriptionPaymentReport,
        search: updatedParams.toString()
      })
    }
  }

  const handleYearBack = () => {
    setSelectedYearData(null)
    setSelectedYearIndex(null)
    setSelectedYear(null)

    setSelectedMonthData(null)
    setFetchedMonthData([])

    setDateRange(null)
    setFilterStates({})
    setFilterValue([])
    setSearchedValue(null)
    setInvoiceNumberSearch(null)

    const updatedParams = new URLSearchParams(location.search)

    updatedParams.delete('year')
    updatedParams.delete('month')

    history.push({
      pathname: ROUTE_STRINGS.subscriptionPaymentReport,
      search: updatedParams.toString()
    })
  }

  const handleMonthBack = () => {
    setSelectedMonthData(null)
    setFetchedMonthData([])
    setDateRange(null)
    setFilterStates({})
    setFilterValue([])
    setSearchedValue(null)
    setInvoiceNumberSearch(null)

    const updatedParams = new URLSearchParams(location.search)

    updatedParams.delete('month')

    history.push({
      pathname: ROUTE_STRINGS.subscriptionPaymentReport,
      search: updatedParams.toString()
    })
  }

  const handleYearChange = (value) => {
    const yearIndex = fetchedYearData?.findIndex((item) => item.year === value)

    if (yearIndex !== -1 && yearIndex !== undefined) {
      const yearData = fetchedYearData[yearIndex]

      setSelectedYearIndex(yearIndex)
      setSelectedYearData(yearData)
      setSelectedYear(value)

      const updatedParams = new URLSearchParams(location.search)

      updatedParams.set('year', value)
      updatedParams.delete('month')

      history.push({
        pathname: ROUTE_STRINGS.subscriptionPaymentReport,
        search: updatedParams.toString()
      })
    }
  }

  const handleDismiss = () => {
    setShowDialog({ ...showDialog, showModal: false })
  }

  const columns = [
    { dataField: 'email', text: t('EMAIL'), className: 'email-address-column' },
    { dataField: 'phone', text: t('PHONE'), className: 'phone-number-column' },
    { dataField: 'idOrder', text: t('ORDER_ID'), className: 'order-id-column' },
    {
      dataField: 'invoiceNumber',
      text: t('INVOICE_NUMBER'),
      className: 'invoice-num-column'
    },
    {
      dataField: 'subName',
      text: t('SUBSCRIPTION_ID'),
      className: 'subscription-id-column'
    },
    {
      dataField: 'invoiceAmount',
      text: t('INVOICE_AMOUNT'),
      className: 'invoice-total-amount-column'
    },
    {
      dataField: 'paymentStatus',
      text: t('PAYMENT_STATUS'),
      className: 'payment-status-column'
    },
    {
      dataField: 'paymentSource',
      text: t('PAYMENT_SOURCE'),
      className: 'payment-source-type-column'
    },
    {
      dataField: 'paymentDate',
      text: t('PAYMENT_DATE'),
      className: 'payment-date-column'
    },
    {
      dataField: 'transactionId',
      text: t('PAYMENT_ID'),
      className: 'transaction-id-column'
    }
  ]

  const excludedColumns = [t('EMAIL'), t('PHONE'), t('SUBSCRIPTION_ID'), t('INVOICE_AMOUNT'), t('PAYMENT_STATUS'), t('PAYMENT_SOURCE'), t('PAYMENT_ID')]

  const columnTypeMap = {
    [t('ORDER_ID')]: 'text',
    [t('INVOICE_NUMBER')]: 'text',
    [t('PAYMENT_DATE')]: 'calendar'
  }

  const dropdownOptions = columns
    ?.filter((column) => !excludedColumns?.includes(column.text))
    ?.map((column) => ({
      value: column.text,
      label: column.text,
      type: columnTypeMap[column.text] || 'text'
    }))

  const handleChange = (e) => {
    setFilterValue(e.value)
  }

  const handleSearch = (field, value) => {
    setFilterStates((prev) => ({
      ...prev,
      [field]: { type: 'text', value }
    }))

    if (field === 'Order Id') {
      setSearchedValue(value)
    }

    if (field === 'Invoice Number') {
      setInvoiceNumberSearch(value)
    }
  }

  const handleDateRangeChange = (field, value) => {
    setFilterStates((prev) => ({
      ...prev,
      [field]: {
        type: 'calendar',
        value
      }
    }))

    setDateRange(value)
  }

  const clearFilter = (filter) => {
    setFilterValue((prev) => prev.filter((item) => item !== filter))

    setFilterStates((prev) => ({
      ...prev,
      [filter]: {
        ...prev[filter],
        value: ''
      }
    }))

    if (filter === 'Order Id') {
      setSearchedValue(null)
    }

    if (filter === 'Invoice Number') {
      setInvoiceNumberSearch(null)
    }
    if (filter === 'Payment Date') {
      setDateRange(null)
    }
  }

  const filteredReport = fetchedMonthData?.filter((item) => {
    const orderIdMatch = searchedValue ? String(item.idOrder).toLowerCase().includes(searchedValue.toLowerCase()) : true

    const invoiceNumberMatch = invoiceNumberSearch ? String(item.invoiceNumber).toLowerCase().includes(invoiceNumberSearch.toLowerCase()) : true

    const isWithinDateRange = (() => {
      if (dateRange?.length === 2 && item.paymentDate) {
        const paymentDate = commonMethods.parseCustomDateFormat(item.paymentDate)
        const [startDate, endDate] = dateRange
        const adjustedEndDate = new Date(endDate)

        adjustedEndDate.setHours(23, 59, 59, 999)

        return paymentDate >= startDate && paymentDate <= adjustedEndDate
      }

      return true
    })()

    return orderIdMatch && invoiceNumberMatch && isWithinDateRange
  })

  const getMonthIndex = (monthName) => {
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

    return monthNames.indexOf(monthName)
  }

  const year = selectedYearData?.year
  const monthIndex = getMonthIndex(selectedMonthData?.name)

  const minDate = year !== undefined && monthIndex >= 0 ? new Date(year, monthIndex, 1) : null
  const maxDate = year !== undefined && monthIndex >= 0 ? new Date(year, monthIndex + 1, 0) : null

  const visibleColumns = columns.filter((col) => (col.dataField === 'email' || col.dataField === 'phone' ? showColumn : columnSettings.visibleColumnsMap[col.dataField]))

  const exportToExcel = () => {
    const dataToExport = filteredReport || []

    exportReportToExcel(dataToExport, columns, (col) => (col.dataField === 'email' || col.dataField === 'phone' ? showColumn : columnSettings.visibleColumnsMap[col.dataField]), {
      sheetName: 'Subscription Payment Reports',
      fileNamePrefix: 'subscription_payment_reports',
      getDateFromItem: (item) => (item.paymentDate ? commonMethods.parseCustomDateFormat(item.paymentDate) : null)
    })
  }

  return (
    <div className="subscription-payment-report">
      <LayoutContainer title={`${t('SUBSCRIPTION_PAYMENT_REPORTS')}${selectedYearData ? ` - ${selectedYear}` : ''}`} breadCrumbsTitle={'subscriptionPaymentReport'} showSettingsButton={!!(selectedYearData && selectedMonthData)} setVisible={columnSettings.openSettings} showDownloadReportButton={!!(selectedYearData && selectedMonthData)} setDisplayButton={exportToExcel}>
        <YearMonthNavigation
          availableReportYears={fetchedYearData}
          availableReportMonths={selectedYearData}
          onYearFolderClick={handleYearClick}
          onMonthFolderClick={handleMonthClick}
          activeYearIndex={selectedYearIndex}
          onYearDecrement={handlePrevious}
          onYearIncrement={handleNext}
          navigateToYearPage={handleYearBack}
          availableYearsList={yearList}
          yearDropdownLabelKey="year"
          yearDropdownValueKey="year"
          yearDropdownValue={selectedYear}
          handleYearDropdownChange={handleYearChange}
          reportTableColumns={visibleColumns}
          reportTableData={filteredReport}
          displaySensitiveColumns={showColumn}
          loading={isLoading}
          activeYear={selectedYearData?.year}
          activeMonth={selectedMonthData?.name}
          navigateToMonthPage={handleMonthBack}
          dropdownFilterOptions={dropdownOptions}
          multiSelectFilterValues={filterValue}
          onMultiSelectFilterChange={handleChange}
          searchFieldValue={filterStates}
          onSearchInputChange={handleSearch}
          handleClearFilters={clearFilter}
          maximumDate={maxDate}
          minimumDate={minDate}
          selectedDateRangeValue={dateRange}
          onDateRangeChange={handleDateRangeChange}
        />
      </LayoutContainer>

      <DialogModal {...showDialog} onDismiss={handleDismiss} />

      <ReportColumnSettingsDialog visible={columnSettings.isSettingsOpen} onHide={columnSettings.handleSettingsClose} columns={columns} activeCheckBox={columnSettings.activeCheckBox} onCheckboxChange={columnSettings.handleCheckboxChange} onSubmit={columnSettings.handleSettingsSubmit} disableSubmit={columnSettings.disableSubmit} messagesRef={columnSettings.messagesRef} columnsFilter={(col) => col.dataField !== 'email' && col.dataField !== 'phone'} />
    </div>
  )
}

export default Loader(SubscriptionPaymentReport)
